import { apiService } from '../../../shared/services/apiServices';
import { EQUIPMENT_ERROR_MESSAGES } from '../errors';
import facilityEndpoints from './facilityEndpoints';
import type {
  FacilityListItem,
  FacilityListResponse,
} from '../types/facilityListType';

export interface ListFacilitiesParams {
  search?: string;
  status?: string;
  page?: number;
  limit?: number;
  facilityIsValid?: boolean;
  equipmentIsValid?: boolean;
  employeeIsValid?: boolean;
}

export interface ListFacilitiesResult {
  success: boolean;
  facilities?: FacilityListItem[];
  totalCount?: number;
  maxFacilityLimit?: number | null;
  currentFacilityCount?: number;
  error?: string;
  forbidden?: boolean;
}

const extractErrorMessage = (errorData: unknown): string => {
  if (!errorData) {
    return EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR;
  }
  if (typeof errorData === 'string') {
    return errorData;
  }
  if (typeof errorData === 'object') {
    const apiError = errorData as { message?: string; error?: string };
    const msg = apiError.message || apiError.error;
    if (msg === 'Internal Server Error') {
      return EQUIPMENT_ERROR_MESSAGES.UNEXPECTED_ERROR;
    }
    return msg || EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR;
  }
  return EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR;
};

/**
 * Filter usage policy for listFacilities across contexts:
 *
 * - Add Client         → no params (omit second arg)
 *                        Draft facilities and all equipment counts are shown so
 *                        the user can assign them during the creation flow.
 *
 * - View Client        → { facilityIsValid: true, equipmentIsValid: true, employeeIsValid: true }
 *                        Only finalised facilities and their valid equipment/employee
 *                        counts are displayed. Drafts are hidden.
 *
 * - Facility List Page → { search, status, page, limit }
 *                        Paginated list with search and status filters.
 *
 * - Edit Client        → TBD (discussion required before implementing).
 */

export const listFacilities = async (
  clientId: string,
  params?: ListFacilitiesParams
): Promise<ListFacilitiesResult> => {
  const queryParams: Record<string, string> = {};
  if (params?.search) {
    queryParams['search'] = params.search;
  }
  if (params?.status) {
    queryParams['status'] = params.status;
  }
  if (params?.page !== undefined) {
    queryParams['page'] = String(params.page);
  }
  if (params?.limit !== undefined) {
    queryParams['limit'] = String(params.limit);
  }
  if (params?.facilityIsValid !== undefined) {
    queryParams['facilityIsValid'] = String(params.facilityIsValid);
  }
  if (params?.equipmentIsValid !== undefined) {
    queryParams['equipmentIsValid'] = String(params.equipmentIsValid);
  }
  if (params?.employeeIsValid !== undefined) {
    queryParams['employeeIsValid'] = String(params.employeeIsValid);
  }

  try {
    const result = await apiService.getAPI(
      facilityEndpoints.FACILITIES(clientId, queryParams),
      { skipGlobalErrorRedirect: true }
    );

    if (result.error) {
      if (result.status === 403) {
        return { success: false, forbidden: true };
      }
      return {
        success: false,
        error: result.isNetworkError
          ? EQUIPMENT_ERROR_MESSAGES.NETWORK_ERROR
          : extractErrorMessage(result.data),
      };
    }

    const data = result.data as FacilityListResponse;
    return {
      success: true,
      facilities: data.data,
      totalCount: data.pagination?.totalCount ?? 0,
      maxFacilityLimit: data.maxFacilityLimit ?? null,
      currentFacilityCount: data.currentFacilityCount ?? 0,
    };
  } catch (err) {
    // AxiosError with no response (network error)
    if (
      typeof err === 'object' &&
      err !== null &&
      'isAxiosError' in err &&
      !('response' in err && (err as { response?: unknown }).response)
    ) {
      return { success: false, error: EQUIPMENT_ERROR_MESSAGES.NETWORK_ERROR };
    }
    // AxiosError with response
    if (
      typeof err === 'object' &&
      err !== null &&
      'isAxiosError' in err &&
      'response' in err
    ) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      const msg = axiosErr.response?.data?.message;
      return {
        success: false,
        error: msg ?? EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR,
      };
    }
    // Generic Error
    if (err instanceof Error) {
      return { success: false, error: err.message };
    }
    return { success: false, error: EQUIPMENT_ERROR_MESSAGES.UNEXPECTED_ERROR };
  }
};
